import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import UtilBar from "@/components/UtilBar";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import QuoteModal from "@/components/QuoteModal";
import PageBreadcrumb from "@/components/PageBreadcrumb";
import InstallationMethods from "@/components/InstallationMethods";
import InstallationPillars from "@/components/InstallationPillars";
import heroImg from "@/assets/lexington-reclaimed-oak-floor-fir-ceiling-great-room.webp";
import usePageSEO from "@/hooks/usePageSEO";
import PageFAQ from "@/components/PageFAQ";
import { installationFAQs } from "@/data/faqData";
import RelatedServices from "@/components/RelatedServices";
import GoogleReviewsCarousel from "@/components/GoogleReviewsCarousel";
import FullServiceCallout from "@/components/FullServiceCallout";

const Installation = () => {
  usePageSEO({
    title: "Hardwood Floor Installation | NWFA Certified Installer",
    description: "NWFA Certified Installer for hardwood floor installation. 5-Inch Rule, subfloor engineering. Livonia showroom.",
    path: "/installation",
    // Service schema baked into static HTML via generate-seo-html.mjs
  });

  return (
    <div className="min-h-screen">
      <UtilBar />
      <SiteHeader />
      <main>
        {/* ══ 1 · HERO ══ */}
        <section className="relative h-[70svh] min-h-[480px] max-h-[720px] flex items-end overflow-hidden">
          <PageBreadcrumb variant="overlay" items={[
            { label: "Home", to: "/" },
            { label: "Services" },
            { label: "Installation" },
          ]} />
          <div className="absolute inset-0">
            <img width={1920} height={1080} loading="eager" decoding="async" fetchPriority="high"
              src={heroImg}
              alt="Great room with a reclaimed hit skip oak floor under a Douglas fir ceiling, Lexington, MI"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-foreground/10" />

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 pb-10 md:pb-16 px-5 md:px-10 lg:px-16 max-w-4xl"
          >
            <p className="text-[10px] md:text-[11px] font-semibold tracking-[.14em] uppercase text-primary mb-3 md:mb-4 flex items-center gap-2">
              <span className="w-7 h-px bg-primary inline-block" />
              Hardwood Installation  Metro Detroit, Michigan
            </p>
            <h1 className="font-display text-[clamp(30px,6.5vw,72px)] font-semibold leading-[1.05] tracking-tight text-primary-foreground">
            Expert Hardwood Floor Installation  Across Metro Detroit
          </h1>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              A successful installation begins long before the first plank is laid. We are professional specifiers and NWFA Certified Installers. We engineer the perfect floor for your home's exact environment, then execute it with precision.
            </p>
            <p className="mt-4 md:mt-6 text-[13px] md:text-[15px] font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              Every floor we install meets or exceeds National Wood Flooring Association (NWFA) industry standard guidelines.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <QuoteModal
                trigger={
                  <button className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:bg-[hsl(var(--gold-400))] transition-colors text-center">
                    Request a Consultation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/25 text-primary-foreground px-7 py-3 rounded-full text-sm font-medium hover:border-primary-foreground/60 transition-colors text-center"
              >
                Call (248) 325-8853
              </a>
            </div>
            <p className="mt-5 text-[13px] md:text-sm font-light text-primary-foreground/60 max-w-lg leading-relaxed">
              Want a new floor sanded and finished on site? See our <Link to="/sand-and-finish" className="text-primary hover:underline">new site finished floors</Link>.
            </p>
          </motion.div>
        </section>

        {/* ══ 2 · THE 5 METHODS OF INSTALLATION ══ */}
        <InstallationMethods />

        {/* ══ 3 · THE 4 PILLARS ══ */}
        <InstallationPillars />

        {/* ══ FULL-SERVICE DIFFERENCE ══ */}
        <FullServiceCallout variant="installation" />

        {/* ══ 4 · CALL TO ACTION ══ */}
        <section className="bg-primary py-10 md:py-14">
          <div className="max-w-[1200px] mx-auto px-5 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-[clamp(24px,3vw,36px)] font-semibold text-primary-foreground leading-[1.1] mb-2">
                Stop guessing. Start <em className="italic font-normal text-[hsl(var(--gold-300))]">specifying.</em>
              </h3>
              <p className="text-[13px] font-light text-primary-foreground/65 leading-relaxed">
                Connect with Michigan's NWFA Certified team. We'll help you choose the right product and ensure it's installed to the standard it deserves.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full md:w-auto">
              <QuoteModal
                trigger={
                  <button className="bg-primary-foreground text-primary px-7 py-3.5 rounded-xl text-xs font-bold tracking-[.05em] uppercase hover:-translate-y-px transition-transform whitespace-nowrap text-center">
                    Schedule a Consultation →
                  </button>
                }
              />
              <a
                href="tel:2483258853"
                className="border border-primary-foreground/30 text-primary-foreground px-7 py-3.5 rounded-xl text-xs font-semibold tracking-[.05em] hover:border-primary-foreground hover:text-primary-foreground transition-colors whitespace-nowrap text-center"
              >
                📞 (248) 325-8853
              </a>
            </div>
          </div>
        </section>
        <div className="max-w-[900px] mx-auto px-5 md:px-12 py-10">
          <Link to="/installation-cost" className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors text-[14px] md:text-[15px]">
            See our complete Michigan hardwood floor installation cost guide →
          </Link>
        </div>
        <RelatedServices
          heading="The installation is only part of the story."
          subheading="Related Services"
          links={[
            { title: "Dust Control Refinishing", desc: "Already have hardwood? Our 98% containment refinishing systems restore existing floors with instant-cure UV finishes.", to: "/refinishing", tag: "Service" },
            { title: "Subfloor Leveling & Repair", desc: "Wide-plank hardwood is unforgiving on uneven subfloors. We engineer the foundation before the first plank.", to: "/subfloor-leveling", tag: "Service" },
            { title: "Environment & Acclimation", desc: "Michigan's climate demands proper moisture science. Learn how we acclimate every floor to your home's ecosystem.", to: "/environment-acclimation", tag: "Technical" },
            { title: "Michigan Plank Flooring", desc: "Go beyond production brands. We manufacture bespoke European White Oak floors in custom colors and extended lengths.", to: "/michigan-plank-flooring", tag: "Brand" },
            { title: "Quick-Step Laminate", desc: "Need a high-performance alternative? Quick-Step's Uniclic technology delivers durability with forensic subfloor prep.", to: "/brands/quick-step", tag: "Brand" },
            { title: "Olde Wood Reclaimed", desc: "Functional fine art. Explore America's most award winning reclaimed timber, installed by NWFA Certified Installers.", to: "/brands/olde-wood", tag: "Brand" },
          ]}
        />

        <GoogleReviewsCarousel contextTags={["installation"]} count={4} />
        <PageFAQ suppressJsonLd faqs={installationFAQs} heading={<>Installation questions <em className="italic font-normal text-primary">answered.</em></>} subheading="Installation FAQs" />
      </main>
      <SiteFooter />
    </div>
  );
};

export default Installation;
